import { useState } from "react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Lock, ShieldCheck, Ban } from "lucide-react";

/**
 * Barre d'état + actions de clôture pour les fiches reprise / devis / intervention.
 * Une fois clôturé, un document n'est plus modifiable ni supprimable : seule une
 * annulation (qui laisse une trace indélébile, jamais un effacement) reste possible.
 */
export function DocumentClosureBar({ module, id, data, canEdit, canDelete, onUpdated, closeLabel = "Clôturer le document" }) {
  const [annulerOpen, setAnnulerOpen] = useState(false);
  const [motif, setMotif] = useState("");
  const [busy, setBusy] = useState(false);

  if (!id || id === "new" || !data) return null;

  const cloturer = async () => {
    setBusy(true);
    try {
      const { data: updated } = await api.post(`/${module}/${id}/cloturer`);
      toast.success("Document clôturé : il n'est plus modifiable");
      onUpdated?.(updated);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const annuler = async () => {
    setBusy(true);
    try {
      const { data: updated } = await api.post(`/${module}/${id}/annuler`, { motif });
      toast.success("Document annulé — la clôture d'origine reste tracée de façon indélébile");
      setAnnulerOpen(false);
      setMotif("");
      onUpdated?.(updated);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (data.status !== "cloture") {
    return canEdit ? (
      <div className="flex justify-end mb-4 no-print">
        <Button data-testid="doc-cloturer-button" variant="outline" onClick={cloturer} disabled={busy} className="gap-2">
          <Lock size={15} /> {closeLabel}
        </Button>
      </div>
    ) : null;
  }

  return (
    <div className="mb-4 no-print space-y-2">
      <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm flex items-start gap-2" data-testid="doc-cloture-banner">
        <ShieldCheck size={16} className="text-emerald-600 shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-emerald-700 dark:text-emerald-400">
            Document clôturé le {data.cloture_date} à {data.cloture_heure} (heure française)
          </p>
          <p className="text-xs text-muted-foreground break-all">Empreinte de sécurité : {data.cloture_hash}</p>
        </div>
      </div>

      {data.annule && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm flex items-start gap-2" data-testid="doc-annule-banner">
          <Ban size={16} className="text-destructive shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-destructive">
              Document annulé le {data.annule_date} à {data.annule_heure} par {data.annule_by_nom}
            </p>
            {data.annule_motif && <p className="text-xs text-muted-foreground">Motif : {data.annule_motif}</p>}
            <p className="text-xs text-muted-foreground break-all">Empreinte de sécurité : {data.annule_hash}</p>
          </div>
        </div>
      )}

      {canDelete && !data.annule && (
        <div className="flex justify-end">
          <Button data-testid="doc-annuler-button" variant="outline" onClick={() => setAnnulerOpen(true)} className="gap-2 text-destructive border-destructive/40 hover:bg-destructive/10">
            <Ban size={15} /> Annuler ce document
          </Button>
        </div>
      )}

      <Dialog open={annulerOpen} onOpenChange={setAnnulerOpen}>
        <DialogContent data-testid="doc-annuler-dialog">
          <DialogHeader><DialogTitle>Annuler ce document ?</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Le document ne sera ni supprimé ni modifié : une mention d'annulation indélébile (horodatage, auteur,
              empreinte de sécurité) sera ajoutée à côté de la clôture d'origine, qui restera toujours visible.
            </p>
            <Textarea data-testid="doc-annuler-motif" placeholder="Motif de l'annulation (optionnel)" value={motif} onChange={(e) => setMotif(e.target.value)} />
          </div>
          <DialogFooter>
            <Button data-testid="doc-annuler-confirm" variant="destructive" onClick={annuler} disabled={busy}>Confirmer l'annulation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
